<template>
	<Multiselect
		ref="multiselect"
		v-model="selected"
		v-bind="$attrs"
		class="input input-select"
		:classes="{
			groupLabel:
				'flex  box-border items-center justify-start text-left py-4 px-8 border-t border-gray-500 uppercase bg-gray-800 cursor-default leading-normal text-gray-400',
			groupLabelPointable: 'cursor-pointer',
			groupLabelPointed: 'bg-gray-800 text-gray-300',
			groupLabelSelected: 'bg-green-600 text-white',
			groupLabelDisabled: 'bg-gray-100 text-gray-300 cursor-not-allowed',
			groupLabelSelectedPointed: 'bg-green-600 text-white opacity-90',
			groupLabelSelectedDisabled: 'text-green-100 bg-green-600/50 cursor-not-allowed',
			tagsSearch:
				'absolute inset-0 border-0 outline-none focus:ring-0 appearance-none p-0 dark:bg-gray-650 bg-white box-border w-full',
		}"
		:options="options"
		:label="labelProp"
		:canClear="clearable"
		:mode="mode"
		:closeOnSelect="closeOnSelectDependingOnProps"
		:placeholder="computedPlaceholder"
		:loading="loading"
		:noOptionsText="noOptionsText"
		:searchable="searchable"
		:trackBy="trackBy"
		@open="onOpen"
		@change="$emit('change', $event)"
		@searchChange="$emit('searchChange', $event)"
		@close="$emit('close', $event)"
	>
		<template v-for="(_, slot) in $slots" v-slot:[slot]="props">
			<slot :name="slot" v-bind="props" />
		</template>
	</Multiselect>
</template>

<script>
import {defineAsyncComponent} from 'vue'

export default {
	components: {
		Multiselect: defineAsyncComponent(() => import('@vueform/multiselect')),
	},
	props: {
		modelValue: {
			type: [String, Number, Array, Object],
			default: null,
		},
		labelProp: {type: String, default: 'label'},
		clearable: Boolean,
		closeOnSelect: {
			type: Boolean,
			default: undefined,
		},
		mode: {
			type: String,
			default: undefined,
			validator: function (value) {
				return [undefined, 'single', 'tags'].includes(value)
			},
		},
		options: {type: [Array, Object, Function], default: () => []},
		loading: Boolean,
		readyPlaceholder: {type: String, default: 'Select an option'},
		noOptionsText: {type: String, default: 'The list is empty'},
		loadingPlaceholder: {type: String, default: 'Loading...'},
		placeholder: {type: String, default: null},
		searchable: Boolean,
		searchBy: {type: [String, Array], default: null},
	},
	emits: ['update:modelValue', 'change', 'searchChange', 'open', 'close'],
	data() {
		return {
			selected: this.modelValue,
		}
	},
	computed: {
		closeOnSelectDependingOnProps() {
			// Allow to override defaults below
			if (this.closeOnSelect !== undefined) return this.closeOnSelect
			// When allowing to select multiple values, keep the value selector open on selection of a value
			if (this.mode === 'tags') return false
			// For "regular" selects, close them by default
			return true
		},
		computedPlaceholder() {
			if (this.placeholder) return this.placeholder
			if (this.loading) return this.loadingPlaceholder
			if (!this.loading && !this.options.length) return this.noOptionsText
			return this.readyPlaceholder
		},
		trackBy() {
			if (!this.searchable) return undefined
			return this.searchBy || this.labelProp
		},
	},
	watch: {
		modelValue(val) {
			this.selected = val
		},
		selected(val) {
			this.$emit('update:modelValue', val)
		},
	},
	methods: {
		async onOpen(select$) {
			this.$emit('open', select$)
			const dropdown = select$.$el.querySelector('.multiselect-dropdown')
			if (!dropdown) return
			await this.$nextTick()
			try {
				dropdown.scrollIntoViewIfNeeded({block: 'end', behavior: 'smooth'})
			} catch (error) {
				// not supported by browser, ignore
				// see https://caniuse.com/scrollintoviewifneeded
				// eslint-disable-next-line no-console
				console.warn('scrollIntoViewIfNeeded not supported by browser')
			}
		},
	},
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
<style>
:root {
	--ms-font-size: var(--font-size-base);
	--ms-bg: #ffffff;
	--ms-bg-disabled: var(--gray-700);
	--ms-border-color: var(--gray-600);
	--ms-border-color-active: var(--gray-500);
	--ms-border-width: 1px;
	--ms-border-width-active: 1px;
	--ms-radius: 4px;
	--ms-py: 0.5rem;
	--ms-px: 8px;
	--ms-ring-width: 0px;
	--ms-ring-color: var(--gray-500);
	--ms-placeholder-color: var(--gray-400);

	--ms-spinner-color: var(--green-500);
	--ms-caret-color: var(--gray-500);
	--ms-clear-color: var(--gray-500);
	--ms-clear-color-hover: var(--gray-100);

	--ms-tag-font-size: var(--font-size-sm);
	--ms-tag-font-weight: 500;
	--ms-tag-bg: var(--gray-500);
	--ms-tag-bg-disabled: #9ca3af;
	--ms-tag-color: #ffffff;
	--ms-tag-color-disabled: #ffffff;
	--ms-tag-radius: 4px;
	--ms-tag-py: 0.125rem;
	--ms-tag-px: 0.5rem;
	--ms-tag-my: 0.25rem;
	--ms-tag-mx: 0.25rem;

	--ms-tag-remove-radius: 4px;
	--ms-tag-remove-py: 0.25rem;
	--ms-tag-remove-px: 0.25rem;
	--ms-tag-remove-my: 0rem;
	--ms-tag-remove-mx: 0.125rem;

	--ms-dropdown-bg: #ffffff;
	--ms-dropdown-border-color: var(--gray-500);
	--ms-dropdown-border-width: 1px;
	--ms-dropdown-radius: 4px;

	--ms-option-bg-pointed: var(--gray-600);
	--ms-option-color-pointed: var(--gray-100);
	--ms-option-bg-selected: var(--gray-500);
	--ms-option-color-selected: var(--gray-100);
	--ms-option-bg-disabled: var(--gray-300);
	--ms-option-color-disabled: var(--gray-200);
	--ms-option-bg-selected-pointed: var(--gray-550);
	--ms-option-color-selected-pointed: var(--gray-100);
	--ms-option-bg-selected-disabled: var(--gray-300);
	--ms-option-color-selected-disabled: var(--gray-200);
	--ms-option-py: 0.5rem;
	--ms-option-px: 0.75rem;

	--ms-empty-color: #4b5563;
}

.dark {
	--ms-bg: var(--gray-650);
	--ms-bg-disabled: var(--gray-500);
	--ms-dropdown-bg: var(--gray-800);
}

.multiselect-tags {
	padding-left: 0;
}

.multiselect-tags-search-wrapper {
	margin: 0 0 var(--ms-tag-my, 4px);
}

.multiselect-dropdown {
	overflow-y: auto;
}
</style>
